import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRINGS = {
  it: {
    confirming: "Conferma in corso...",
    ok_title_new: "Garanzia registrata!",
    ok_title_already: "Registrazione già confermata",
    ok_desc_new: "Grazie! La tua registrazione della garanzia è stata confermata con successo.",
    ok_desc_already: "Questa registrazione è stata confermata in precedenza. Non è necessario alcun ulteriore intervento.",
    install_date_label: "Data installazione registrata",
    not_applied_note: "Nota: la data di installazione era già stata confermata in precedenza. I tuoi contatti sono stati comunque aggiornati.",
    help: "In caso di problemi tecnici, contatta l'assistenza attraverso il QR code sul dispenser.",
    err_title: "Link non valido",
    err_desc: "Il link di conferma non è valido o è scaduto. Se hai bisogno di aiuto contatta il rivenditore.",
    footer: "© Wineemotion · Assistenza tecnica",
  },
  en: {
    confirming: "Confirming...",
    ok_title_new: "Warranty registered!",
    ok_title_already: "Registration already confirmed",
    ok_desc_new: "Thank you! Your warranty registration has been successfully confirmed.",
    ok_desc_already: "This registration was already confirmed earlier. No further action is required.",
    install_date_label: "Registered installation date",
    not_applied_note: "Note: the installation date had already been confirmed previously. Your contact info has still been updated.",
    help: "In case of technical issues, contact support via the QR code on the dispenser.",
    err_title: "Invalid link",
    err_desc: "The confirmation link is invalid or has expired. If you need help please contact your reseller.",
    footer: "© Wineemotion · Technical support",
  },
};

function useLang() {
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("wm_public_lang");
    if (stored === "it" || stored === "en") return stored;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    return nav === "it" ? "it" : "en";
  });
  return [lang, (l) => { setLang(l); localStorage.setItem("wm_public_lang", l); }];
}

/**
 * Public warranty confirmation landing page (bilingual IT/EN).
 * URL: `/garanzia/conferma/:token` — reached from the double-opt-in email link.
 */
export default function WarrantyConfirm() {
  const { token } = useParams();
  const [lang, setLang] = useLang();
  const t = STRINGS[lang];
  const [state, setState] = useState({ loading: true, ok: false, data: null, error: "" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/warranty/confirm/${encodeURIComponent(token)}`);
        setState({ loading: false, ok: true, data, error: "" });
      } catch (e) {
        setState({ loading: false, ok: false, data: null, error: e.response?.data?.detail || "" });
      }
    })();
  }, [token]);

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4 py-10">
      <div className="max-w-lg w-full">
        <div className="flex justify-center mb-6">
          <BrandLogo />
        </div>
        <div className="flex justify-end mb-4" data-testid="public-lang-switcher">
          <div className="inline-flex rounded-sm border border-zinc-200 bg-white text-[10px] uppercase tracking-widest">
            <button type="button" data-testid="public-lang-it" onClick={() => setLang("it")}
              className={`px-3 py-1.5 ${lang === "it" ? "bg-brand text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>🇮🇹 IT</button>
            <button type="button" data-testid="public-lang-en" onClick={() => setLang("en")}
              className={`px-3 py-1.5 ${lang === "en" ? "bg-brand text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>🇬🇧 EN</button>
          </div>
        </div>
        <Card className="p-8 rounded-sm bg-white border-zinc-200" data-testid="warranty-confirm-page">
          {state.loading && (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-brand mx-auto mb-3" />
              <div className="text-sm text-zinc-600">{t.confirming}</div>
            </div>
          )}

          {!state.loading && state.ok && (
            <div className="text-center" data-testid="warranty-confirm-ok">
              <div className="flex justify-center mb-4">
                <div className="h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <ShieldCheck className="h-7 w-7" strokeWidth={1.75} />
                </div>
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900 mb-2">
                {state.data.already_confirmed ? t.ok_title_already : t.ok_title_new}
              </h1>
              <p className="text-sm text-zinc-600 leading-relaxed mb-4">
                {state.data.already_confirmed ? t.ok_desc_already : t.ok_desc_new}
              </p>
              {Array.isArray(state.data.results) && state.data.results.length > 0 && (
                <div className="text-left rounded-sm border border-zinc-200 bg-zinc-50 p-3 mb-3 space-y-1" data-testid="warranty-confirm-results">
                  {state.data.results.map((r) => (
                    <div key={r.serial} className="flex items-center justify-between gap-3 text-xs">
                      <div className="min-w-0">
                        <div className="font-mono text-zinc-900 truncate">{r.serial}</div>
                        {r.model && <div className="text-zinc-500 truncate">{r.model}</div>}
                      </div>
                      <div className="shrink-0">
                        {r.status === "confirmed" && <span className="rounded-sm border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-emerald-800 text-[10px] uppercase tracking-widest">✓ {new Date(r.install_date_current).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</span>}
                        {r.status === "already_confirmed" && <span className="rounded-sm border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-amber-900 text-[10px] uppercase tracking-widest">{lang === "it" ? "già confermato" : "already set"}</span>}
                        {r.status === "not_found" && <span className="rounded-sm border border-red-200 bg-red-50 px-1.5 py-0.5 text-red-800 text-[10px] uppercase tracking-widest">✗</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-zinc-500 mt-6">{t.help}</p>
            </div>
          )}

          {!state.loading && !state.ok && (
            <div className="text-center" data-testid="warranty-confirm-error">
              <div className="flex justify-center mb-4">
                <div className="h-14 w-14 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
                  <XCircle className="h-7 w-7" strokeWidth={1.75} />
                </div>
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900 mb-2">{t.err_title}</h1>
              <p className="text-sm text-zinc-600 leading-relaxed">{state.error || t.err_desc}</p>
            </div>
          )}
        </Card>
        <p className="text-center text-xs text-zinc-500 mt-6">{t.footer}</p>
      </div>
    </div>
  );
}
